/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../../root'
import { useNavigateWithParams } from '../../../../hooks/useNavigateWithParams'
import { useModal } from '../../../../contexts/ModalContext'
import ActionsMenu from '../../../../components/shared/ActionsMenu'
import { WorkflowStepType } from '../../types'
import { AclLevel } from '../../../../constants/fsi'
import { toast } from '../../../../services/toast'
import { Task } from '../../../../services/api/types'
import { FileDiffModal } from '../../../../components/modals/FileDiffModal'
import { FsiPropertiesModal } from '../../../../components/modals/FsiPropertiesModal'
import { UploadFileModal } from '../../../../components/modals/UploadFileModal'
import FsiPickerModal from '../../../../components/modals/FsiPickerModal'
import UploadFileRequestModal from '~/pages/browser/upload-file/UploadFileRequestModal'

interface TaskFile {
  draftFsi?: any
  sourceFsi?: any
  targetFsi?: any
}

interface TaskManageDocsTableMenuProps {
  file: TaskFile
  task: Task | null
  workflow: any
  doClose?: () => void
}

interface MenuItemData {
  action?: () => void
  value?: string
  disabled?: boolean
  submenu?: MenuItemData[]
}

/**
 * Task Manage Documents Table Menu
 * Actions menu for individual files in multi-file workflows
 * Migrated from: lego/page/tasks/manage-docs/TableMenu.jsx
 */
export default function TaskManageDocsTableMenu({ file, task, workflow, doClose }: TaskManageDocsTableMenuProps) {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const { showModal } = useModal()
  const navigate = useNavigateWithParams()
  const [items, setItems] = useState<MenuItemData[]>([])
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState(false)
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null)

  useEffect(() => {
    const menu: MenuItemData[] = []

    if (file.draftFsi) {
      menu.push({
        value: 'VIEW_DRAFT_VERSION',
        action: () => {
          navigate(`/show-file/${file.draftFsi.id}`)
          doClose?.()
        }
      })

      if (task?.flags?.canChangeProperties) {
        menu.push({
          value: 'BROWSER_ACTION_PROPERTIES',
          action: () => {
            showModal(
              <FsiPropertiesModal
                fsiId={file.draftFsi.id}
                onSuccess={() => {
                  toast.success(t('PROPERTIES_UPDATED'))
                }}
                onClose={() => {}}
              />
            )
          }
        })
      }
    }

    if ((file.sourceFsi || file.targetFsi) && file.draftFsi) {
      menu.push({
        value: 'VIEW_CHANGES',
        action: () => {
          showModal(<FileDiffModal fileId={file.draftFsi.id} onClose={() => {}} />)
        }
      })

      menu.push({
        value: 'VIEW_EFFECTIVE_VERSION',
        action: () => {
          const fileId = file.targetFsi ? file.targetFsi.id : file.sourceFsi.id
          navigate(`/browser/file/${fileId}`)
        }
      })
    }

    if (
      task?.workflowStepType === WorkflowStepType.REVISE_EDIT_MULTIFILE ||
      task?.workflowStepType === WorkflowStepType.REVISE_EDIT_MIGRATION
    ) {
      menu.push({
        value: 'BROWSER_ACTION_DOWNLOAD_SOURCE_DOC',
        action: async () => {
          const fileId = file.draftFsi ? file.draftFsi.id : file.sourceFsi.id
          try {
            const { url, s3Url, message } = await api.files.getFileDownloadUrl(fileId)
            const downloadUrl = s3Url || url
            if (downloadUrl) {
              window.open(downloadUrl, '_blank', 'noopener')
              // toast.success(t('DOWNLOAD_STARTED'))
            } else {
              toast.error(message || t('DOWNLOAD_FAILED'))
            }
          } catch (error: any) {
            toast.error(error.message || t('DOWNLOAD_FAILED'))
          }
        }
      })
    }

    if (
      [AclLevel.WRITE, AclLevel.DELETE].includes(file.draftFsi?.aclLevel) &&
      (task?.workflowStepType === WorkflowStepType.REVISE_EDIT_MULTIFILE ||
        task?.workflowStepType === WorkflowStepType.REVISE_EDIT_MIGRATION)
    ) {
      if (file.draftFsi) {
        const draftId = file.draftFsi.id

        if (task.flags?.canUploadFile) {
          const subMenuUpload: MenuItemData[] = []

          if (task.flags?.canUploadFromLocalPC) {
            subMenuUpload.push({
              value: 'FROM_MY_COMPUTER',
              action: () => {
                if (typeof task.workflowDraftFolderId === 'number') {
                  showModal(
                    <UploadFileRequestModal
                      parentId={task.workflowDraftFolderId}
                      params={{
                        workflowId: workflow?.id,
                        task: task
                      }}
                      onSuccess={() => {}}
                      onFailure={() => {}}
                    />
                  )
                } else {
                  toast.error(t('NO_FOLDER_SPECIFIED'))
                }
              }
            })

            subMenuUpload.push({
              value: 'FROM_RCM',
              action: () => {
                setCurrentFolderId(task.workflowDraftFolderId || 0)
                setIsFsiPickerOpen(true)
              }
            })
          }

          if (task.flags?.canUploadFromGDrive) {
            subMenuUpload.push({
              value: 'FROM_GOOGLE_DRIVE',
              action: async () => {
                try {
                  await api.files.uploadFromGoogleDrive(draftId)
                  toast.success(t('DOC_UPLOADED_MSG'))
                } catch (error: any) {
                  toast.error(error.message || t('UPLOAD_FAILED'))
                }
              }
            })
          }

          if (task.flags?.canUploadFromOneDrive) {
            subMenuUpload.push({
              value: 'DOWNLOAD_FROM_OFFICE_365',
              action: async () => {
                try {
                  await api.files.uploadFromOneDrive(draftId)
                  toast.success(t('BROWSER_UPLOAD_FILE_O365_SUCCESS_MSG'))
                } catch (error: any) {
                  toast.error(error.message || t('UPLOAD_FAILED'))
                }
              }
            })
          }

          if (subMenuUpload.length > 0) {
            menu.push({ value: 'ACTION_COMMAND_UPLOAD_DOCUMENT', submenu: subMenuUpload })
          }

          // Edit in Google Docs/Sheets
          const fileExtension = file.draftFsi.name?.split('.').pop()?.toLowerCase()
          if (task.flags?.canUploadFromGDrive) {
            let editAction = null
            if (['doc', 'docx', 'txt'].includes(fileExtension || '')) {
              editAction = 'EDIT_IN_GOOGLE_DOCS_ACTION'
            } else if (['csv', 'xls', 'xlsx'].includes(fileExtension || '')) {
              editAction = 'EDIT_IN_GOOGLE_SHEETS_ACTION'
            }

            if (editAction) {
              menu.push({
                value: editAction,
                action: async () => {
                  try {
                    await api.files.editInGoogleDocs(draftId)
                  } catch (error: any) {
                    toast.error(error.message || t('EDIT_FAILED'))
                  }
                }
              })
            }
          }

          // Edit in Office 365
          if (task.flags?.canUploadFromOneDrive) {
            menu.push({
              value: 'EDIT_IN_OFFICE_365_ACTION',
              action: async () => {
                try {
                  await api.files.editInOffice365(draftId, fileExtension)
                } catch (error: any) {
                  if (error.message === 'EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED') {
                    toast.error(t('EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED'))
                  } else {
                    toast.error(error.message || t('EDIT_FAILED'))
                  }
                }
              }
            })
          }
        }
      }
    }

    setItems(menu)
  }, [file, task, workflow, api, navigate, showModal, t])

  const handleDocumentSelect = async (fsi: any) => {
    if (!fsi || !fsi.id || !currentFolderId) {
      setIsFsiPickerOpen(false)
      setCurrentFolderId(null)
      return
    }

    try {
      // Call API to upload version from existing document
      await api.files.uploadVersionFromExistingDocument(fsi.id, currentFolderId)
      toast.success(t('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
    } catch (error: any) {
      console.error('Upload from RCM failed:', error)
      toast.error(error.message || t('UPLOAD_FAILED'))
    } finally {
      setIsFsiPickerOpen(false)
      setCurrentFolderId(null)
    }
  }

  return (
    <>
      <ActionsMenu menuId={`manage-docs-menu-${file.draftFsi?.id}`} items={items} />

      {isFsiPickerOpen && (
        <FsiPickerModal
          options={{
            selector: 'file',
            showRC: true,
            showSystem: false
          }}
          title={t('SELECT_DOCUMENT_FROM_RCM')}
          isOpen={isFsiPickerOpen}
          onClose={() => {
            setIsFsiPickerOpen(false)
            setCurrentFolderId(null)
          }}
          onSelect={handleDocumentSelect}
        />
      )}
    </>
  )
}
